import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import type { ScopeVersion } from '@ai-secure-twin/shared';
import { Icon } from '../../components/Icon';
import { api, ApiError } from '../../lib/api';
import { AgentRunPanel } from './AgentRunPanel';
import { ApprovalCard } from './ApprovalCard';
import { ApprovalHistory } from './ApprovalHistory';
import { DefineScopeDialog } from './DefineScopeDialog';
import { EngagementCommand } from './EngagementCommand';
import { FindingsList } from './FindingsList';
import { ScanPanel } from './ScanPanel';
import { NextStep } from './NextStep';
import { RemediationPanel } from './RemediationPanel';
import { ReportsPanel } from './ReportsPanel';
import { StageBadge, StageTrack } from './StageStatus';

/** Stages in which a scope may still be written; past these the approved version is the authorisation. */
const SCOPE_EDITABLE = new Set(['DRAFT', 'SCOPE_PENDING_APPROVAL']);

const formatInstant = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function ScopePanel({ scope }: { scope: ScopeVersion }) {
  return (
    <div className="scope-view">
      <div className="scope-view-row">
        <span className="scope-label">Allowed hosts</span>
        <ul className="scope-list">
          {scope.targets.allowedHosts.map((host) => (
            <li key={host} className="mono">
              {host}
            </li>
          ))}
        </ul>
      </div>
      <div className="scope-view-row">
        <span className="scope-label">Seed URLs</span>
        <ul className="scope-list">
          {scope.targets.seedUrls.map((url) => (
            <li key={url} className="mono">
              {url}
            </li>
          ))}
        </ul>
      </div>
      {scope.targets.excludePaths.length > 0 ? (
        <div className="scope-view-row">
          <span className="scope-label">Excluded</span>
          <ul className="scope-list">
            {scope.targets.excludePaths.map((path) => (
              <li key={path} className="mono">
                {path}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="scope-view-row">
        <span className="scope-label">Limits</span>
        <span>
          {scope.limits.maxRequestsPerSecond} req/s · up to {scope.limits.maxDurationMinutes} min
        </span>
      </div>
      <div className="scope-view-row">
        <span className="scope-label">Window</span>
        <span>
          {scope.windows
            .map((w) => `${formatInstant(w.startsAt)} — ${formatInstant(w.endsAt)}`)
            .join(', ')}
        </span>
      </div>
      <div className="scope-view-row">
        <span className="scope-label">Permits</span>
        <span>
          {[
            scope.permits.activeScan ? 'active scanning' : null,
            scope.permits.fuzzing ? 'fuzzing' : null,
          ]
            .filter(Boolean)
            .join(', ') || 'passive only'}
        </span>
      </div>
      <div className="scope-view-row">
        <span className="scope-label">Authorised</span>
        <span>
          {scope.authorisation.reference} · {scope.authorisation.authorisedByName} ·{' '}
          {formatInstant(scope.authorisation.authorisedAt)}
        </span>
      </div>
    </div>
  );
}

export function EngagementDetailPage() {
  const { id = '' } = useParams();
  const [scopeOpen, setScopeOpen] = useState(false);
  const [saved, setSaved] = useState<number | null>(null);

  const engagement = useQuery({
    queryKey: ['engagement', id],
    queryFn: () => api.getEngagement(id),
    enabled: id.length > 0,
  });

  const scope = useQuery({
    queryKey: ['scope', id],
    queryFn: () => api.listScopeVersions(id),
    enabled: id.length > 0,
  });

  /*
   * An application accepted from discovery (ADR 0008) offers its hosts to the first scope. Only
   * the first: a revision starts from the approved scope, never from discovery.
   */
  const application = useQuery({
    queryKey: ['application', engagement.data?.applicationId],
    queryFn: () => api.getApplication(engagement.data!.applicationId),
    enabled: Boolean(engagement.data?.applicationId) && (scope.data?.items.length ?? 1) === 0,
  });
  const discoveredHosts =
    application.data?.origin?.kind === 'DISCOVERY_PROPOSAL'
      ? [...new Set(application.data.components.flatMap((c) => c.hosts))]
      : [];

  /*
   * The same keys the panels below use, so the progress track shares their cache rather than
   * fetching everything twice — and moves when they do.
   */
  const scans = useQuery({
    queryKey: ['scans', id],
    queryFn: () => api.listScans(id),
    enabled: id.length > 0,
  });
  const findings = useQuery({
    queryKey: ['findings', id],
    queryFn: () => api.listFindings(id),
    enabled: id.length > 0,
  });
  const approvals = useQuery({
    queryKey: ['approvalHistory', id],
    queryFn: () => api.listApprovals(id),
    enabled: id.length > 0,
  });

  if (engagement.isLoading) {
    return (
      <div className="page">
        <p className="muted">Loading engagement…</p>
      </div>
    );
  }

  if (engagement.isError || !engagement.data) {
    const notFound = engagement.error instanceof ApiError && engagement.error.status === 404;
    return (
      <div className="page">
        <div className="card empty">
          <h1 className="serif" style={{ fontSize: 32 }}>
            {notFound ? 'Engagement not found' : 'Could not load this engagement'}
          </h1>
          {!notFound && engagement.error instanceof ApiError ? (
            <p className="muted">{engagement.error.message}</p>
          ) : null}
          <Link to="/app/engagements" className="btn btn-outline">
            Back to engagements
          </Link>
        </div>
      </div>
    );
  }

  const e = engagement.data;
  const versions = scope.data?.items ?? [];
  const current = versions.at(-1);
  const editable = SCOPE_EDITABLE.has(e.stage);

  // The approved version is the authority, not the latest draft.
  const approvedScope = versions.find((v) => v.version === e.currentScopeVersion);
  const evidence = {
    scans: scans.data?.items ?? [],
    findings: findings.data?.items ?? [],
    approvals: approvals.data?.items ?? [],
    activeScanPermitted: approvedScope?.permits.activeScan ?? false,
    ...(e.currentScopeVersion !== undefined ? { scopeVersion: e.currentScopeVersion } : {}),
  };

  return (
    <div className="page">
      <Link
        to="/app/engagements"
        className="muted"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
      >
        <Icon name="back" size={16} /> Engagements
      </Link>

      <div className="page-head">
        <div>
          <span className="eyebrow">
            <span className="mono">{e.id}</span> · {e.environment}
          </span>
          <h1>{e.name}</h1>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <StageBadge stage={e.stage} />
        </div>
      </div>

      {saved ? (
        <p className="banner-ok" role="status">
          Scope v{saved} saved. This engagement is now awaiting approval — nothing will be tested
          until someone approves it.
        </p>
      ) : null}

      <EngagementCommand engagement={e} />

      <section className="card panel">
        <h2 className="panel-title">Lifecycle</h2>
        <StageTrack stage={e.stage} evidence={evidence} />
        <NextStep engagement={e} evidence={evidence} />
      </section>

      <section className="card panel" aria-labelledby="scope-title">
        <div className="panel-head">
          <h2 id="scope-title" className="panel-title">
            Scope{current ? ` · v${current.version}` : ''}
          </h2>
          {editable ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setScopeOpen(true)}
            >
              {current ? 'Revise scope' : 'Define scope'}
            </button>
          ) : (
            <span className="muted" style={{ fontSize: 12 }}>
              Locked — the approved version is the authorisation
            </span>
          )}
        </div>

        {scope.isLoading ? (
          <p className="muted">Loading scope…</p>
        ) : current ? (
          <>
            <ScopePanel scope={current} />
            {versions.length > 1 ? (
              <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
                {versions.length} versions. Earlier ones are kept — scope history is append-only.
              </p>
            ) : null}
          </>
        ) : (
          <div className="empty">
            <p>No scope defined.</p>
            <p className="muted">
              A scope names the hosts that may be tested, how hard, and when. Until one exists and
              is approved, this engagement does nothing.
            </p>
          </div>
        )}
      </section>

      <ApprovalCard engagement={e} />

      <ApprovalHistory engagementId={e.id} />

      <ScanPanel engagement={e} />

      <AgentRunPanel engagement={e} />

      <FindingsList engagement={e} />

      <RemediationPanel engagement={e} />

      <ReportsPanel engagement={e} />

      {scopeOpen ? (
        <DefineScopeDialog
          engagement={e}
          suggestedHosts={discoveredHosts}
          onClose={() => setScopeOpen(false)}
          onSaved={(version) => {
            setSaved(version);
            setScopeOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
